import type { ReactNode } from 'react';
import type { CalcRun, CalcStep } from '@ave/shared';
import { money, moneyShort, pct, shortDate } from '../lib/format';

/**
 * Report exhibits.
 *
 * Every chart here plots numbers the calculation engine produced, against
 * the evidence or corridor that justifies them. Nothing is computed in the
 * browser: where a chart needs a figure that is not already in a run's
 * outputs (the sensitivity grid, for instance), that figure comes from its
 * own calculation run, because a number a reader might rely on has to be
 * traceable to the calculation log like any other.
 *
 * The visual grammar is consistent across exhibits, so a reader learns it
 * once: teal is the adopted position, a pale teal band is the range that
 * supports it, rose is a deduction, amber marks a judgement that needs
 * defending, and slate is a total.
 */

const C = {
  adopted: '#0d9488',
  adoptedDark: '#0f766e',
  band: '#ccfbf1',
  bandEdge: '#5eead4',
  deduct: '#e11d48',
  add: '#059669',
  total: '#0f172a',
  judgement: '#f59e0b',
  evidence: '#64748b',
  grid: '#e2e8f0',
  axis: '#94a3b8',
  faint: '#f1f5f9',
};

export interface ChartData {
  runs: (CalcRun & { steps: CalcStep[] })[];
  evidencePoints: {
    id: string;
    metric: string;
    value: string;
    unit: string | null;
    sectorCode: string | null;
    asAt: string;
    sourceName: string;
    quality: string;
  }[];
  shareTransactions: {
    id: string;
    txnDate: string;
    pricePerShare: string;
    counterpartyRelationship: string;
  }[];
  facts: { provenance: string }[];
  valuationDate: string;
  currency: string;
}

const out = (data: ChartData, fn: string): Record<string, any> | undefined =>
  [...data.runs].reverse().find((r) => r.functionName === fn && r.status === 'ok')?.outputs as
    | Record<string, any>
    | undefined;

const inp = (data: ChartData, fn: string): Record<string, any> | undefined =>
  [...data.runs].reverse().find((r) => r.functionName === fn && r.status === 'ok')?.inputs as
    | Record<string, any>
    | undefined;

// ---------------------------------------------------------------------
// Figure chrome
// ---------------------------------------------------------------------

export function Figure({
  number,
  title,
  source,
  children,
}: {
  number: number;
  title: string;
  source?: string;
  children: ReactNode;
}) {
  return (
    <figure className="my-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
      <figcaption className="border-b border-slate-100 bg-slate-50/70 px-4 py-2.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-teal-700">
          Figure {number}
        </span>
        <span className="ml-2 text-sm font-medium text-slate-800">{title}</span>
      </figcaption>
      <div className="px-4 py-4">{children}</div>
      {source ? (
        <p className="border-t border-slate-100 px-4 py-2 text-[11px] leading-relaxed text-slate-400">
          {source}
        </p>
      ) : null}
    </figure>
  );
}

function Legend({ items }: { items: { colour: string; label: string; shape?: 'box' | 'line' | 'dot' }[] }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-1.5 text-[11px] text-slate-500">
          {it.shape === 'line' ? (
            <span className="h-0.5 w-4 rounded" style={{ background: it.colour }} />
          ) : it.shape === 'dot' ? (
            <span className="h-2 w-2 rounded-full" style={{ background: it.colour }} />
          ) : (
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: it.colour }} />
          )}
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// 1. NAV composition
// ---------------------------------------------------------------------

export function NavCompositionChart({ data }: { data: ChartData }) {
  const inputs = inp(data, 'nav_assembly');
  const outputs = out(data, 'nav_assembly');
  if (!inputs || !outputs) return null;

  const assets = (inputs.assets ?? []).map((a: any) => ({
    label: String(a.label),
    value: Number(a.value),
  }));
  const total = assets.reduce((s: number, a: { value: number }) => s + a.value, 0);
  if (total <= 0) return null;

  const palette = [C.adopted, '#0891b2', '#6366f1', '#8b5cf6'];
  let x = 0;

  return (
    <div>
      <svg viewBox="0 0 640 74" className="h-auto w-full" role="img" aria-label="Gross asset composition">
        {assets.map((a: { label: string; value: number }, i: number) => {
          const w = (a.value / total) * 640;
          const rect = (
            <g key={a.label}>
              <rect
                x={x}
                y={0}
                width={Math.max(w - 2, 1)}
                height={30}
                rx={3}
                fill={palette[i % palette.length]}
              />
              {w > 92 ? (
                <>
                  <text x={x + 8} y={19} fill="#fff" fontSize={11} fontWeight={600}>
                    {moneyShort(a.value, data.currency)}
                  </text>
                  <text x={x + 1} y={46} fill="#475569" fontSize={10.5}>
                    {a.label.length > 34 ? `${a.label.slice(0, 33)}…` : a.label}
                  </text>
                  <text x={x + 1} y={60} fill="#94a3b8" fontSize={10}>
                    {((a.value / total) * 100).toFixed(1)}% of gross assets
                  </text>
                </>
              ) : null}
            </g>
          );
          x += w;
          return rect;
        })}
      </svg>
      <p className="mt-1 text-xs text-slate-500">
        Gross assets of{' '}
        <span className="tnum font-medium text-slate-800">
          {money(outputs.gross_assets, data.currency)}
        </span>
        , before liabilities of {money(outputs.total_liabilities, data.currency)} and the latent tax
        deduction.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------
// 2. Latent tax position
// ---------------------------------------------------------------------

export function LatentTaxPositionChart({ data }: { data: ChartData }) {
  const o = out(data, 'latent_tax');
  if (!o) return null;

  const full = Number(o.full_latent_tax);
  const deducted = Number(o.deduction);
  const applied = Number(o.applied_deduction_pct);
  const min = Number(o.corridor?.min ?? 0.3);
  const max = Number(o.corridor?.max ?? 0.7);
  const recommended = Number(o.recommended_deduction_pct ?? 0.5);

  const W = 640;
  const barY = 8;
  const barH = 34;
  const deductedW = full > 0 ? (deducted / full) * W : 0;

  // Corridor scale below the bar, drawn across 0–100% of the full charge.
  const scaleY = 86;
  const at = (p: number) => p * W;

  return (
    <div>
      <svg viewBox="0 0 640 138" className="h-auto w-full" role="img" aria-label="Latent tax position">
        {/* full charge */}
        <rect x={0} y={barY} width={W} height={barH} rx={4} fill={C.faint} stroke={C.grid} />
        <rect x={0} y={barY} width={Math.max(deductedW, 2)} height={barH} rx={4} fill={C.deduct} opacity={0.85} />
        <text x={8} y={barY + 22} fill="#fff" fontSize={12} fontWeight={600}>
          {money(deducted, data.currency)} deducted
        </text>
        <text x={W - 8} y={barY + 22} textAnchor="end" fill="#64748b" fontSize={11}>
          full charge {money(full, data.currency)}
        </text>

        {/* corridor scale */}
        <text x={0} y={scaleY - 12} fill="#94a3b8" fontSize={10.5}>
          Deduction as a percentage of the full charge
        </text>
        <line x1={0} x2={W} y1={scaleY} y2={scaleY} stroke={C.grid} strokeWidth={6} strokeLinecap="round" />
        <line
          x1={at(min)}
          x2={at(max)}
          y1={scaleY}
          y2={scaleY}
          stroke={C.bandEdge}
          strokeWidth={6}
          strokeLinecap="round"
        />
        <line
          x1={at(recommended)}
          x2={at(recommended)}
          y1={scaleY - 9}
          y2={scaleY + 9}
          stroke={C.adopted}
          strokeWidth={1.5}
          strokeDasharray="3 2"
        />
        <circle cx={at(applied)} cy={scaleY} r={6.5} fill={C.judgement} stroke="#fff" strokeWidth={2} />

        <text x={at(min)} y={scaleY + 22} textAnchor="middle" fill="#64748b" fontSize={10}>
          {(min * 100).toFixed(0)}%
        </text>
        <text x={at(max)} y={scaleY + 22} textAnchor="middle" fill="#64748b" fontSize={10}>
          {(max * 100).toFixed(0)}%
        </text>
        <text
          x={Math.min(Math.max(at(applied), 22), W - 22)}
          y={scaleY - 14}
          textAnchor="middle"
          fill="#b45309"
          fontSize={11}
          fontWeight={600}
        >
          {(applied * 100).toFixed(0)}% adopted
        </text>
      </svg>

      <Legend
        items={[
          { colour: C.deduct, label: 'Deduction adopted' },
          { colour: C.bandEdge, label: 'Corridor for this purpose', shape: 'line' },
          { colour: C.adopted, label: 'Recommended position', shape: 'line' },
          { colour: C.judgement, label: 'Position adopted', shape: 'dot' },
        ]}
      />
      {o.challenge_flag ? (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          This position is flagged as challenge-likely under a fiscal purpose.
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// 3. Latent tax sensitivity (line)
// ---------------------------------------------------------------------

export function LatentTaxSensitivityChart({ data }: { data: ChartData }) {
  const o = out(data, 'holding_sensitivity');
  const curve = (o?.latent_tax_curve ?? []) as { pct: string; holding_value: string }[];
  if (curve.length < 2) return null;

  const applied = Number(out(data, 'latent_tax')?.applied_deduction_pct ?? 0.5);
  const W = 640;
  const H = 190;
  const pad = { l: 62, r: 16, t: 14, b: 34 };

  const xs = curve.map((p) => Number(p.pct));
  const ys = curve.map((p) => Number(p.holding_value));
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys) * 0.985;
  const yMax = Math.max(...ys) * 1.015;

  const X = (v: number) => pad.l + ((v - xMin) / (xMax - xMin || 1)) * (W - pad.l - pad.r);
  const Y = (v: number) => H - pad.b - ((v - yMin) / (yMax - yMin || 1)) * (H - pad.t - pad.b);

  const path = curve.map((p, i) => `${i === 0 ? 'M' : 'L'}${X(Number(p.pct))},${Y(Number(p.holding_value))}`).join(' ');
  const area = `${path} L${X(xMax)},${H - pad.b} L${X(xMin)},${H - pad.b} Z`;
  const atApplied = curve.reduce((best, p) =>
    Math.abs(Number(p.pct) - applied) < Math.abs(Number(best.pct) - applied) ? p : best,
  );

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Latent tax sensitivity">
        {[0, 0.5, 1].map((f) => {
          const v = yMin + (yMax - yMin) * f;
          return (
            <g key={f}>
              <line x1={pad.l} x2={W - pad.r} y1={Y(v)} y2={Y(v)} stroke={C.grid} strokeWidth={1} />
              <text x={pad.l - 8} y={Y(v) + 3.5} textAnchor="end" fill="#94a3b8" fontSize={10}>
                {moneyShort(v, data.currency)}
              </text>
            </g>
          );
        })}

        <path d={area} fill={C.adopted} opacity={0.08} />
        <path d={path} fill="none" stroke={C.adopted} strokeWidth={2.25} strokeLinejoin="round" />

        {curve.map((p) => (
          <circle
            key={p.pct}
            cx={X(Number(p.pct))}
            cy={Y(Number(p.holding_value))}
            r={3}
            fill="#fff"
            stroke={C.adopted}
            strokeWidth={1.75}
          />
        ))}

        <line
          x1={X(Number(atApplied.pct))}
          x2={X(Number(atApplied.pct))}
          y1={pad.t}
          y2={H - pad.b}
          stroke={C.judgement}
          strokeWidth={1.5}
          strokeDasharray="4 3"
        />
        <circle
          cx={X(Number(atApplied.pct))}
          cy={Y(Number(atApplied.holding_value))}
          r={5.5}
          fill={C.judgement}
          stroke="#fff"
          strokeWidth={2}
        />

        {curve.map((p) => (
          <text
            key={`x${p.pct}`}
            x={X(Number(p.pct))}
            y={H - pad.b + 15}
            textAnchor="middle"
            fill="#94a3b8"
            fontSize={10}
          >
            {(Number(p.pct) * 100).toFixed(0)}%
          </text>
        ))}
        <text x={(W + pad.l) / 2} y={H - 4} textAnchor="middle" fill="#64748b" fontSize={10.5}>
          Latent tax deduction adopted
        </text>
      </svg>

      <p className="mt-1 text-xs text-slate-500">
        Across the full corridor the value of the holding moves from{' '}
        <span className="tnum font-medium text-slate-800">
          {money(curve[curve.length - 1]!.holding_value, data.currency)}
        </span>{' '}
        to <span className="tnum font-medium text-slate-800">{money(curve[0]!.holding_value, data.currency)}</span>{' '}
        — the measure of how much this valuation rests on the latent-tax argument.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------
// 4. Discount evidence
// ---------------------------------------------------------------------

export function DiscountEvidenceChart({ data }: { data: ChartData }) {
  const inputs = inp(data, 'holding_value') ?? inp(data, 'discounts');
  const block = inputs?.discounts ?? inputs;
  if (!block?.dloc && !block?.dlom) return null;

  const rows = [
    { key: 'dloc', label: 'Lack of control (DLOC)', metric: 'dloc_observation', d: block.dloc },
    { key: 'dlom', label: 'Lack of marketability (DLOM)', metric: 'dlom_observation', d: block.dlom },
  ].filter((r) => r.d);

  const W = 640;
  const rowH = 56;
  const pad = { l: 8, r: 8, t: 16 };
  const axisMax = 0.8;
  const X = (v: number) => pad.l + (v / axisMax) * (W - pad.l - pad.r);

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${rows.length * rowH + 44}`}
        className="h-auto w-full"
        role="img"
        aria-label="Discounts against evidence ranges"
      >
        {[0, 0.2, 0.4, 0.6, 0.8].map((t) => (
          <g key={t}>
            <line
              x1={X(t)}
              x2={X(t)}
              y1={pad.t - 6}
              y2={rows.length * rowH + 8}
              stroke={C.grid}
              strokeWidth={1}
            />
            <text
              x={X(t)}
              y={rows.length * rowH + 24}
              textAnchor="middle"
              fill="#94a3b8"
              fontSize={10}
            >
              {(t * 100).toFixed(0)}%
            </text>
          </g>
        ))}

        {rows.map((row, i) => {
          const y = pad.t + i * rowH + 18;
          const lo = Number(row.d.range_low ?? 0);
          const hi = Number(row.d.range_high ?? 0);
          const sel = Number(row.d.selected ?? 0);
          const observations = data.evidencePoints.filter((e) => e.metric === row.metric);

          return (
            <g key={row.key}>
              <text x={pad.l} y={y - 12} fill="#334155" fontSize={11} fontWeight={600}>
                {row.label}
              </text>

              {hi > lo ? (
                <>
                  <rect x={X(lo)} y={y - 8} width={X(hi) - X(lo)} height={16} rx={3} fill={C.band} />
                  <line x1={X(lo)} x2={X(lo)} y1={y - 10} y2={y + 10} stroke={C.bandEdge} strokeWidth={1.75} />
                  <line x1={X(hi)} x2={X(hi)} y1={y - 10} y2={y + 10} stroke={C.bandEdge} strokeWidth={1.75} />
                  <text x={X(lo) - 4} y={y + 4} textAnchor="end" fill="#64748b" fontSize={9.5}>
                    {(lo * 100).toFixed(0)}%
                  </text>
                  <text x={X(hi) + 4} y={y + 4} fill="#64748b" fontSize={9.5}>
                    {(hi * 100).toFixed(0)}%
                  </text>
                </>
              ) : null}

              {observations.map((o, oi) => (
                <circle
                  key={o.id}
                  cx={X(Number(o.value))}
                  cy={y + (oi % 2 === 0 ? -0.5 : 0.5) * 6}
                  r={3.5}
                  fill={C.evidence}
                  opacity={0.55}
                >
                  <title>{`${o.sourceName} — ${(Number(o.value) * 100).toFixed(1)}% (${o.quality.replace(/_/g, ' ')}, ${o.asAt})`}</title>
                </circle>
              ))}

              <g transform={`translate(${X(sel)}, ${y})`}>
                <path d="M0,-13 L6,-4 L-6,-4 Z" fill={C.adoptedDark} />
                <line x1={0} x2={0} y1={-4} y2={12} stroke={C.adoptedDark} strokeWidth={2} />
                <text y={26} textAnchor="middle" fill={C.adoptedDark} fontSize={11} fontWeight={700}>
                  {(sel * 100).toFixed(0)}%
                </text>
              </g>
            </g>
          );
        })}
      </svg>

      <Legend
        items={[
          { colour: C.band, label: 'Evidence range applied' },
          { colour: C.evidence, label: 'Published observations', shape: 'dot' },
          { colour: C.adoptedDark, label: 'Discount adopted', shape: 'line' },
        ]}
      />

      <div className="mt-3 space-y-2">
        {rows.map((row) =>
          row.d.justification ? (
            <p key={row.key} className="text-xs leading-relaxed text-slate-500">
              <span className="font-semibold text-slate-700">{row.label.split(' (')[0]}:</span>{' '}
              {row.d.justification}
            </p>
          ) : null,
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// 5. Discount sensitivity heatmap
// ---------------------------------------------------------------------

export function DiscountSensitivityHeatmap({ data }: { data: ChartData }) {
  const o = out(data, 'holding_sensitivity');
  const grid = (o?.grid ?? []) as { dloc: string; dlom: string; holding_value: string }[];
  if (grid.length === 0) return null;

  const dlocs = [...new Set(grid.map((g) => g.dloc))].sort((a, b) => Number(a) - Number(b));
  const dloms = [...new Set(grid.map((g) => g.dlom))].sort((a, b) => Number(a) - Number(b));
  const values = grid.map((g) => Number(g.holding_value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const base = o?.base_cell as { dloc: string; dlom: string; holding_value: string } | undefined;

  const lookup = new Map(grid.map((g) => [`${g.dloc}|${g.dlom}`, g]));

  // Pale teal (low value) to deep teal (high value).
  const colour = (v: number) => {
    const t = max > min ? (v - min) / (max - min) : 0.5;
    const lightness = 96 - t * 52;
    return `hsl(174, 62%, ${lightness}%)`;
  };
  const textColour = (v: number) => {
    const t = max > min ? (v - min) / (max - min) : 0.5;
    return t > 0.55 ? '#ffffff' : '#0f172a';
  };

  const cw = 88;
  const ch = 30;
  const left = 62;
  const top = 30;
  const W = left + dloms.length * cw + 6;
  const H = top + dlocs.length * ch + 26;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        style={{ minWidth: Math.min(W, 640) }}
        role="img"
        aria-label="Holding value across discount ranges"
      >
        <text x={left} y={11} fill="#64748b" fontSize={10.5}>
          DLOM →
        </text>
        {dloms.map((dlom, j) => (
          <text
            key={dlom}
            x={left + j * cw + cw / 2}
            y={24}
            textAnchor="middle"
            fill="#475569"
            fontSize={10.5}
            fontWeight={600}
          >
            {(Number(dlom) * 100).toFixed(0)}%
          </text>
        ))}

        <text x={4} y={11} fill="#64748b" fontSize={10.5}>
          DLOC ↓
        </text>

        {dlocs.map((dloc, i) =>
          dloms.map((dlom, j) => {
            const cell = lookup.get(`${dloc}|${dlom}`);
            if (!cell) return null;
            const v = Number(cell.holding_value);
            const isBase = base && base.dloc === dloc && base.dlom === dlom;
            return (
              <g key={`${dloc}-${dlom}`}>
                <rect
                  x={left + j * cw}
                  y={top + i * ch}
                  width={cw - 2}
                  height={ch - 2}
                  rx={2}
                  fill={colour(v)}
                  stroke={isBase ? C.judgement : 'transparent'}
                  strokeWidth={isBase ? 2.5 : 0}
                />
                <text
                  x={left + j * cw + (cw - 2) / 2}
                  y={top + i * ch + 19}
                  textAnchor="middle"
                  fill={textColour(v)}
                  fontSize={10.5}
                  fontWeight={isBase ? 700 : 400}
                >
                  {moneyShort(v, data.currency)}
                </text>
              </g>
            );
          }),
        )}

        {dlocs.map((dloc, i) => (
          <text
            key={`r${dloc}`}
            x={left - 8}
            y={top + i * ch + 19}
            textAnchor="end"
            fill="#475569"
            fontSize={10.5}
            fontWeight={600}
          >
            {(Number(dloc) * 100).toFixed(0)}%
          </text>
        ))}
      </svg>

      <p className="mt-2 text-xs text-slate-500">
        The position adopted is outlined. Across the whole of both evidence ranges the value of the
        holding runs from{' '}
        <span className="tnum font-medium text-slate-800">{money(min, data.currency)}</span> to{' '}
        <span className="tnum font-medium text-slate-800">{money(max, data.currency)}</span>
        {o?.span?.spread_pct
          ? `, a spread of ${pct(o.span.spread_pct, 0)} of the figure adopted.`
          : '.'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------
// 6. Earnings weighting
// ---------------------------------------------------------------------

export function EarningsWeightingChart({ data }: { data: ChartData }) {
  const o = out(data, 'maintainable_earnings');
  const periods = (o?.normalised_by_period ?? []) as {
    label: string;
    reported_earnings?: string;
    adjustments_total?: string;
    normalised_earnings: string;
    normalised_weight?: string;
  }[];
  if (periods.length === 0) return null;

  const weighted = Number(o?.weighted_maintainable_earnings ?? 0);
  const W = 640;
  const H = 200;
  const pad = { l: 62, r: 16, t: 16, b: 44 };
  const max = Math.max(...periods.map((p) => Number(p.normalised_earnings)), weighted) * 1.12;
  const Y = (v: number) => H - pad.b - (v / max) * (H - pad.t - pad.b);
  const colW = (W - pad.l - pad.r) / periods.length;
  const barW = Math.min(colW * 0.44, 62);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Earnings by period">
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={pad.l} x2={W - pad.r} y1={Y(max * f)} y2={Y(max * f)} stroke={C.grid} />
            <text x={pad.l - 8} y={Y(max * f) + 3.5} textAnchor="end" fill="#94a3b8" fontSize={10}>
              {moneyShort(max * f, data.currency)}
            </text>
          </g>
        ))}

        {periods.map((p, i) => {
          const cx = pad.l + colW * i + colW / 2;
          const reported = Number(p.reported_earnings ?? p.normalised_earnings);
          const normalised = Number(p.normalised_earnings);
          const weight = Number(p.normalised_weight ?? 0);
          return (
            <g key={p.label}>
              <rect
                x={cx - barW / 2}
                y={Y(reported)}
                width={barW}
                height={Math.max(H - pad.b - Y(reported), 1)}
                rx={2}
                fill={C.grid}
              />
              <rect
                x={cx - barW / 2}
                y={Y(normalised)}
                width={barW}
                height={Math.max(H - pad.b - Y(normalised), 1)}
                rx={2}
                fill={C.adopted}
                opacity={0.25 + weight * 0.75}
              />
              <text x={cx} y={Y(normalised) - 6} textAnchor="middle" fill="#0f766e" fontSize={10.5} fontWeight={600}>
                {moneyShort(normalised, data.currency)}
              </text>
              <text x={cx} y={H - pad.b + 15} textAnchor="middle" fill="#475569" fontSize={10.5}>
                {p.label}
              </text>
              {p.normalised_weight ? (
                <text x={cx} y={H - pad.b + 29} textAnchor="middle" fill="#94a3b8" fontSize={10}>
                  weight {(weight * 100).toFixed(0)}%
                </text>
              ) : null}
            </g>
          );
        })}

        <line
          x1={pad.l}
          x2={W - pad.r}
          y1={Y(weighted)}
          y2={Y(weighted)}
          stroke={C.total}
          strokeWidth={1.75}
          strokeDasharray="5 3"
        />
        <text x={W - pad.r} y={Y(weighted) - 6} textAnchor="end" fill={C.total} fontSize={10.5} fontWeight={700}>
          maintainable {moneyShort(weighted, data.currency)}
        </text>
      </svg>

      <Legend
        items={[
          { colour: C.grid, label: 'Reported' },
          { colour: C.adopted, label: 'Normalised (opacity shows weighting)' },
          { colour: C.total, label: 'Weighted maintainable earnings', shape: 'line' },
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------
// 7. Comparable multiples
// ---------------------------------------------------------------------

export function ComparableMultiplesChart({ data }: { data: ChartData }) {
  const inputs = inp(data, 'capitalised_earnings');
  if (!inputs) return null;
  const adopted = Number(inputs.multiple);
  const comps = data.evidencePoints.filter(
    (e) => e.metric === 'ebitda_multiple' || e.metric === 'deal_multiple',
  );

  const all = [...comps.map((c) => Number(c.value)), adopted];
  const lo = Math.min(...all) * 0.75;
  const hi = Math.max(...all) * 1.15;
  const W = 640;
  const H = 96;
  const pad = { l: 16, r: 16 };
  const X = (v: number) => pad.l + ((v - lo) / (hi - lo || 1)) * (W - pad.l - pad.r);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Comparable multiples">
        <line x1={pad.l} x2={W - pad.r} y1={44} y2={44} stroke={C.grid} strokeWidth={5} strokeLinecap="round" />

        {comps.map((c, i) => (
          <g key={c.id}>
            <circle cx={X(Number(c.value))} cy={44 + (i % 2 === 0 ? -1 : 1) * 7} r={5} fill={C.evidence} opacity={0.5}>
              <title>{`${c.sourceName} — ${c.value}x (${c.sectorCode ?? 'all sectors'}, ${c.asAt})`}</title>
            </circle>
          </g>
        ))}

        <g transform={`translate(${X(adopted)}, 44)`}>
          <path d="M0,-20 L7,-9 L-7,-9 Z" fill={C.adoptedDark} />
          <line x1={0} x2={0} y1={-9} y2={16} stroke={C.adoptedDark} strokeWidth={2.25} />
          <text y={32} textAnchor="middle" fill={C.adoptedDark} fontSize={12} fontWeight={700}>
            {adopted.toFixed(1)}x adopted
          </text>
        </g>

        <text x={pad.l} y={18} fill="#94a3b8" fontSize={10}>
          {lo.toFixed(1)}x
        </text>
        <text x={W - pad.r} y={18} textAnchor="end" fill="#94a3b8" fontSize={10}>
          {hi.toFixed(1)}x
        </text>
      </svg>

      {inputs.multiple_justification ? (
        <p className="mt-1 text-xs leading-relaxed text-slate-500">{inputs.multiple_justification}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// 8. Transaction timeline
// ---------------------------------------------------------------------

export function TransactionTimelineChart({ data }: { data: ChartData }) {
  const txns = data.shareTransactions;
  const W = 640;
  const H = 108;
  const pad = { l: 24, r: 24 };
  const valuation = new Date(data.valuationDate).getTime();

  if (txns.length === 0) {
    return (
      <p className="py-3 text-sm text-slate-500">
        No transactions in the subject shares were identified. The hunt covered SH01 filings, the
        company’s own records and the interview; finding none is a valid outcome, and the valuation
        proceeds on the modelled bases set out above.
      </p>
    );
  }

  const times = [...txns.map((t) => new Date(t.txnDate).getTime()), valuation];
  const min = Math.min(...times);
  const max = Math.max(...times);
  const span = max - min || 1;
  const X = (t: number) => pad.l + ((t - min) / span) * (W - pad.l - pad.r);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Transaction timeline">
        <line x1={pad.l} x2={W - pad.r} y1={54} y2={54} stroke={C.grid} strokeWidth={2} />

        <g transform={`translate(${X(valuation)}, 0)`}>
          <line x1={0} x2={0} y1={22} y2={72} stroke={C.judgement} strokeWidth={1.75} strokeDasharray="4 3" />
          <text y={16} textAnchor="middle" fill="#b45309" fontSize={10.5} fontWeight={600}>
            valuation date
          </text>
        </g>

        {txns.map((t) => {
          const x = X(new Date(t.txnDate).getTime());
          const armsLength = t.counterpartyRelationship === 'arms_length';
          return (
            <g key={t.id} transform={`translate(${x}, 54)`}>
              <circle r={7} fill={armsLength ? C.adopted : C.evidence} stroke="#fff" strokeWidth={2}>
                <title>{`${t.txnDate} — ${money(t.pricePerShare)} per share (${t.counterpartyRelationship.replace(/_/g, ' ')})`}</title>
              </circle>
              <text y={-14} textAnchor="middle" fill="#0f766e" fontSize={11} fontWeight={600}>
                {money(t.pricePerShare)}
              </text>
              <text y={24} textAnchor="middle" fill="#64748b" fontSize={10}>
                {shortDate(t.txnDate)}
              </text>
              <text y={37} textAnchor="middle" fill="#94a3b8" fontSize={9.5}>
                {t.counterpartyRelationship.replace(/_/g, ' ')}
              </text>
            </g>
          );
        })}
      </svg>
      <Legend
        items={[
          { colour: C.adopted, label: 'Arm’s length', shape: 'dot' },
          { colour: C.evidence, label: 'Connected or unknown', shape: 'dot' },
          { colour: C.judgement, label: 'Valuation date', shape: 'line' },
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------
// 9. Provenance summary
// ---------------------------------------------------------------------

const PROVENANCE_ORDER = [
  { key: 'registry_confirmed', label: 'Registry confirmed', colour: '#059669' },
  { key: 'external_expert', label: 'External expert', colour: '#0284c7' },
  { key: 'corroborated', label: 'Corroborated', colour: '#0d9488' },
  { key: 'data_derived', label: 'Data derived', colour: '#64748b' },
  { key: 'management_asserted', label: 'Management asserted', colour: '#f59e0b' },
];

export function ProvenanceSummaryChart({ data }: { data: ChartData }) {
  const counts = PROVENANCE_ORDER.map((p) => ({
    ...p,
    n: data.facts.filter((f) => f.provenance === p.key).length,
  })).filter((p) => p.n > 0);

  const total = counts.reduce((s, c) => s + c.n, 0);
  if (total === 0) return null;

  const W = 640;
  let x = 0;

  return (
    <div>
      <svg viewBox={`0 0 ${W} 30`} className="h-auto w-full" role="img" aria-label="Provenance of facts relied upon">
        {counts.map((c) => {
          const w = (c.n / total) * W;
          const rect = (
            <rect key={c.key} x={x} y={0} width={Math.max(w - 2, 1)} height={26} rx={3} fill={c.colour}>
              <title>{`${c.label}: ${c.n} of ${total}`}</title>
            </rect>
          );
          x += w;
          return rect;
        })}
      </svg>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {counts.map((c) => (
          <span key={c.key} className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: c.colour }} />
            {c.label}
            <span className="tnum text-slate-400">
              {c.n} ({((c.n / total) * 100).toFixed(0)}%)
            </span>
          </span>
        ))}
      </div>
      {counts.some((c) => c.key === 'management_asserted') ? (
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          Facts asserted by management and not independently corroborated are rendered in the
          narrative as such — the report says “we were informed by management that…” rather than
          stating them as established.
        </p>
      ) : null}
    </div>
  );
}
